import React, { ReactNode, useEffect } from "react";
import { SvgArrowLeftIcon, SvgArrowRightIcon, SvgCloseIcon } from "../SvgIcons";
import "./styles/DesignSystemModal.module.css";
import { css } from "@emotion/css";

type Props = {
  show: boolean;
  onCloseModal: (event: React.MouseEvent<HTMLDivElement>) => void;
  onCloseModalWithBtn: () => void;
  isCenterRequired?: boolean;
  children?: ReactNode;
  width?: string;
  height?: string;
  navButtonState?: any;
};

const DesignSystemModal = ({
  show,
  onCloseModal,
  onCloseModalWithBtn,
  width,
  height,
  navButtonState,
  children,
}: Props) => {
  const modalStyle = show
    ? css`
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        z-index: 1;
      `
    : css`
        display: none;
      `;

  /**
   * Handle keyboard Escape for modal
   */
  useEffect(() => {
    if (!show) return;

    const closeModal = (event: KeyboardEvent) => {
      if (event.key === "Escape" && show) {
        onCloseModalWithBtn();
      }
    };

    window.removeEventListener("keyup", closeModal);
    window.addEventListener("keyup", closeModal);
    return () => {
      window.removeEventListener("keyup", closeModal);
    };
  }, [show]);

  return (
    <div className={modalStyle}>
      <div
        data-value="modalOverlay"
        className={css`
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.25);
          position: relative;
          z-index: -1;

          @media only screen and (min-width: 500px) and (max-width: 1023px) {
            min-height: 100%;
            overflow: auto;
            padding-top: 105px;
            height: 100%;
          }

          @media only screen and (max-width: 500px) {
            min-height: 100%;
            overflow: auto;
            height: 100%;
          }
        `}
        onClick={(event) => onCloseModal(event)}
      >
        <div
          data-close="modalCloseButton"
          className={css`
            position: fixed;
            right: 20px;
            top: 20px;
            cursor: pointer;

            @media only screen and (min-width: 500px) and (max-width: 1023px) {
              display: none;
            }

            @media only screen and (max-width: 500px) {
              z-index: 20;
              opacity: 0.6;
            }
          `}
          onClick={() => onCloseModalWithBtn()}
        >
          <SvgCloseIcon size="36rem" />
        </div>
        <div
          className={modalItemContainer}
          style={{
            maxWidth: width && width,
            height: height && height,
          }}
        >
          {children}
          {navButtonState?.left?.canGoBack && (
            <div
              className={modalArrowLeft}
              onClick={() => navButtonState.goPrev()}
            >
              <SvgArrowLeftIcon />
            </div>
          )}

          {navButtonState?.right?.canGoNext && (
            <div
              tabIndex={0}
              className={modalArrowRight}
              onClick={() => navButtonState.goNext()}
            >
              <SvgArrowRightIcon />
            </div>
          )}
          <div
            data-close="modalCloseButton"
            className={css`
              display: none;

              @media only screen and (min-width: 500px) and (max-width: 1023px) {
                cursor: pointer;
                display: block;
                right: 0;
                position: absolute;
                top: -50px;
              }
            `}
            onClick={() => onCloseModalWithBtn()}
          >
            <SvgCloseIcon size="30px" />
          </div>
        </div>
      </div>
      ;
    </div>
  );
};

const modalItemContainer = css`
  position: fixed;
  background: #ffffff;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
  border-radius: 32px;
  max-width: 944px;
  width: 100%;
  height: 480px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin-left: 10px;
  margin-right: 10px;

  @media only screen and (max-width: 1307px) {
    max-width: 895px;
    height: 480px;
    width: 85vw;
    margin: 0 auto;
  }

  @media only screen and (min-width: 500px) and (max-width: 1023px) {
    width: 86%;
    height: 100%;
    max-width: 464px;
    transform: unset;
    position: relative;
    top: unset;
    left: unset;
    border-radius: 20px;
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
  }

  @media only screen and (max-width: 500px) {
    height: 100%;
    width: 100%;
    transform: unset;
    position: relative;
    top: unset;
    left: unset;
    border-radius: 20px;
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
    margin: unset;
  }
`;

const modalArrowLeft = css`
  position: fixed;
  cursor: pointer;
  width: 48px;
  height: 48px;
  top: 50%;
  left: -8%;
  transform: translate(8%, -50%);

  @media only screen and (min-width: 500px) and (max-width: 1023px) {
    position: absolute;
    width: 40px;
    height: 40px;
    left: 0;
    top: -50px;
    transform: unset;
  }

  @media only screen and (max-width: 500px) { 
    position: absolute;
    width: 40px;
    height: 40px;
    left: 0;
    top: 20px;
    transform: unset;
    opacity: 0.6;
  }
  }
`;

const modalArrowRight = css`
  position: fixed;
  cursor: pointer;
  width: 48px;
  height: 48px;
  top: 50%;
  right: -8%;
  transform: translate(8%, -50%);

  @media only screen and (min-width: 500px) and (max-width: 1023px) {
    position: absolute;
    width: 40px;
    height: 40px;
    top: -50px;
    left: 15%;
    transform: unset;
  }

  @media only screen and (max-width: 500px) {
    position: absolute;
    width: 40px;
    height: 40px;
    top: 20px;
    left: 15%;
    transform: unset;
    opacity: 0.6;
  }
`;

export default DesignSystemModal;
